import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const NAMESPACED_KEY = /^[a-z]+:[\w.-]+$/i;

function isPlain(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

/** Walks react-hook-form's nested error tree; `ref` holds DOM nodes and is left alone. */
function translateErrors(errors: unknown, t: (key: string) => string): unknown {
  if (Array.isArray(errors)) return errors.map((entry) => translateErrors(entry, t));
  if (!isPlain(errors)) return errors;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(errors)) {
    if (key === 'ref') out[key] = value;
    else if (key === 'message' && typeof value === 'string' && NAMESPACED_KEY.test(value)) {
      out[key] = t(value);
    } else out[key] = translateErrors(value, t);
  }
  return out;
}

/**
 * zodResolver whose messages are i18n keys (`auth:validation.email`) translated on the way out,
 * so schemas stay free of React and FormMessage renders words, not keys.
 */
export function useI18nResolver<TInput extends FieldValues, TOutput>(
  schema: z.ZodType<TOutput, TInput>,
): Resolver<TInput, unknown, TOutput> {
  const { t } = useTranslation();
  return useMemo(() => {
    const base = zodResolver(schema);
    const resolver: Resolver<TInput, unknown, TOutput> = async (values, context, options) => {
      const result = await base(values, context, options);
      if (Object.keys(result.errors).length === 0) return result;
      return { values: {}, errors: translateErrors(result.errors, t) as FieldErrors<TInput> };
    };
    return resolver;
  }, [schema, t]);
}
